// 路由跳转拦截处理，防止连续点击等
import { useRouter } from 'next/compat/router';
import { useCallback, useEffect, useState } from 'react';

interface UseRouteChangeOptions {
  onStart?: () => void;
  onComplete?: () => void;
  onError?: (error: Error) => void;
}

interface UseRouteChangeReturn {
  isRouteChanging: boolean;
  navigate: (callback: () => void | Promise<void>) => Promise<void>;
}

export const useRouteChange = ({
  onStart,
  onComplete,
  onError,
}: UseRouteChangeOptions = {}): UseRouteChangeReturn => {
  const router = useRouter();
  const [isRouteChanging, setIsRouteChanging] = useState(false);
  useEffect(() => {
    if (!router) {
      return;
    }
    const handleStart = () => {
      setIsRouteChanging(true);
      onStart?.();
    };

    const handleComplete = (url: string) => {
      setIsRouteChanging(false);

      onComplete?.();
    };

    const handleError = (error: Error) => {
      setIsRouteChanging(false);

      onError?.(error);
      console.error('Navigation error:', error);
    };

    router.events.on('routeChangeStart', handleStart);
    router.events.on('routeChangeComplete', handleComplete);
    router.events.on('routeChangeError', handleError);

    return () => {
      router.events.off('routeChangeStart', handleStart);
      router.events.off('routeChangeComplete', handleComplete);
      router.events.off('routeChangeError', handleError);
    };
  }, [router, onStart, onComplete, onError]);

  const navigate = useCallback(
    async (callback: () => void | Promise<void>) => {
      if (isRouteChanging) return;

      try {
        setIsRouteChanging(true);
        const result = callback();
        if (result instanceof Promise) {
          await result;
        }
      } catch (error) {
        setIsRouteChanging(false);
        throw error;
      } finally {
        setIsRouteChanging(false);
      }
    },
    [router, isRouteChanging],
  );

  return { isRouteChanging, navigate };
};
